import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout } from './SiteChrome.js';

/* Thin outlined circle icons, in the wireframe's style. */
function CircleIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="circle-icon" aria-hidden="true">
      <svg viewBox="0 0 32 32">{children}</svg>
    </span>
  );
}

const IconWave = (
  <>
    <line x1="10" y1="13" x2="10" y2="19" />
    <line x1="14" y1="9" x2="14" y2="23" />
    <line x1="18" y1="11" x2="18" y2="21" />
    <line x1="22" y1="14" x2="22" y2="18" />
  </>
);

const IconCheck = <path d="M10 16.5l4 4 8-9" />;

/*
 * Signature motif: a field of uniform, anonymous "voices" (identical dots — no one
 * is distinguishable) that flows into an aggregated, rising signal of "value" (bars).
 * It's the confidentiality promise, drawn: individuals stay anonymous, the whole speaks.
 */
const DOT_COLS = [20, 54, 88, 122, 156, 190];
const DOT_ROWS = [26, 58, 90, 122, 154];
const BAR_HEIGHTS = [40, 58, 50, 78, 96, 86, 120, 142];
const BASELINE = 186;

function VoiceToValueMotif() {
  return (
    <div className="v2v-motif" aria-hidden="true">
      <svg viewBox="0 0 640 200" role="presentation">
        {DOT_ROWS.map((cy, r) =>
          DOT_COLS.map((cx, c) => (
            <circle key={`${r}-${c}`} className="dot" cx={cx} cy={cy} r={4} />
          )),
        )}

        <path className="flow" d="M198 60 C 270 60, 300 150, 352 150" fill="none" />
        <path className="flow" d="M198 90 C 270 90, 300 150, 352 150" fill="none" />
        <path className="flow" d="M198 120 C 270 120, 300 150, 352 150" fill="none" />

        {BAR_HEIGHTS.map((h, i) => (
          <rect
            key={i}
            className="bar"
            x={356 + i * 34}
            y={BASELINE - h}
            width={24}
            height={h}
            rx={3}
          />
        ))}
      </svg>
      <div className="motif-labels">
        <span>Many voices</span>
        <span>One clear signal</span>
      </div>
    </div>
  );
}

const PILLARS = [
  {
    title: 'Anonymity you control',
    body: 'Choose how anonymous each survey is, from fully anonymous to named responses, so the level always fits your team and the question you’re asking.',
  },
  {
    title: 'Trends and themes',
    body: 'See clear trends and themes across your organisation, so you know what’s working, what’s changed, and where to focus next.',
  },
  {
    title: 'Never shared',
    body: 'Data is handled securely and used only to understand engagement. Responses are never sold or shared for marketing.',
  },
];

export function LandingPage() {
  useEffect(() => {
    document.title = 'Voice to Value | Employee engagement surveys';
  }, []);

  return (
    <SiteLayout>
      {/* Hero — two-column split, lilac | white */}
      <section className="v2v-hero">
        <div className="hero-grid">
          <div className="hero-col hero-lilac">
            <p className="eyebrow">Employee engagement surveys</p>
            <h1>
              From Employee <span className="accent">Voice</span> to Business{' '}
              <span className="accent">Value</span>
            </h1>
            <p className="lead">
              Voice to Value provides employee engagement surveys, with the level of anonymity you
              choose, that help organisations understand what matters most to their people. Through
              confidential feedback and expert analysis, we uncover insights that drive engagement,
              improve culture, and support better business outcomes.
            </p>
            <div className="cta-row">
              <Link className="btn btn-primary" to="/contact">
                Contact me
              </Link>
              <Link className="btn btn-outline" to="/privacy">
                How anonymity works
              </Link>
            </div>
          </div>

          <div className="hero-col hero-white">
            <div className="hero-aside">
              <CircleIcon>{IconWave}</CircleIcon>
              <p className="tagline">
                Your people have the answers. We&rsquo;ll help you hear them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature motif */}
      <section className="v2v-motif-section">
        <div className="shell">
          <div className="divider" />
          <h2>From many voices, one clear signal.</h2>
          <VoiceToValueMotif />
        </div>
      </section>

      {/* Who we are — opens with the vision, then the narrative */}
      <section className="v2v-about-section tone-lilac" id="about">
        <div className="shell about-shell">
          <div className="divider" />
          <p className="eyebrow">Who we are</p>
          <h2>Giving employees a voice. Creating value through action.</h2>
          <p className="about-vision">
            Our vision is to create workplaces where every employee knows their voice matters, every
            leader has meaningful insight, and every organisation has the tools to turn feedback into
            measurable value.
          </p>
          <div className="about-body">
            <p>
              At Voice to Value, we believe that listening to employees is only the beginning of
              meaningful organisational change.
            </p>
            <p>
              For a number of years, our team have been helping organisations gather honest,
              confidential employee feedback through professionally designed staff surveys. During
              that time, we&rsquo;ve worked with businesses, charities, and other third-sector
              organisations to understand what truly matters to their people.
            </p>
            <p>
              Our approach goes beyond measuring employee sentiment. We support organisations through
              the entire engagement journey, helping leaders understand feedback, identify
              priorities, develop action plans, and create lasting improvements that employees can see
              and experience.
            </p>
          </div>
        </div>
      </section>

      {/* Our story */}
      <section className="v2v-about-section tone-white">
        <div className="shell about-shell">
          <div className="divider" />
          <p className="eyebrow">Our story</p>
          <h2>From listening to lasting change.</h2>
          <div className="about-body">
            <p>
              Having provided employee survey services for many years, we saw first-hand the impact
              that listening can have on organisational performance and employee engagement.
            </p>
            <p>
              However, we also saw that many organisations struggled with the critical next step:
              translating feedback into meaningful change.
            </p>
            <p>
              In response, we developed the Voice to Value Engagement Programme, a structured process
              that takes organisations beyond survey results and focuses on collaboration, action, and
              continuous improvement.
            </p>
            <p>
              Today, our services help organisations not only understand the voice of their employees
              but also demonstrate that every voice has the potential to create value.
            </p>
          </div>
        </div>
      </section>

      {/* What makes us different */}
      <section className="v2v-about-section tone-lilac">
        <div className="shell about-shell">
          <div className="divider" />
          <p className="eyebrow">What makes us different</p>
          <h2>
            Many organisations can provide a survey. We provide a complete employee engagement
            solution.
          </h2>
          <p className="about-lead">Our Voice to Value approach helps organisations:</p>
          <ul className="v2v-checklist">
            <li>Gather honest and meaningful employee feedback</li>
            <li>Understand the themes and drivers behind the results</li>
            <li>Engage managers and teams in constructive conversations</li>
            <li>Develop practical and achievable action plans</li>
            <li>Measure progress and demonstrate improvement over time</li>
          </ul>
          <p className="about-close">
            Because employees are more likely to engage when they see action resulting from their
            feedback, we focus on creating a continuous cycle of listening, responding, and improving.
          </p>
        </div>
      </section>

      {/* Commitment card on a lilac band */}
      <section className="v2v-commit">
        <div className="shell">
          <div className="commit-card">
            <p className="eyebrow">Our commitment</p>
            <h2>Confidentiality is the foundation of honest feedback.</h2>
            <p>
              Honest feedback happens when people feel safe to give it. You decide how anonymous each
              survey is, and every response is handled securely and with care.
            </p>
            <Link className="btn btn-primary" to="/privacy">
              Read our commitment
            </Link>
          </div>
        </div>
      </section>

      {/* Trust pillars — feature cards */}
      <section className="v2v-trust">
        <div className="shell">
          <div className="section-head">
            <div className="divider" />
            <h2>Why teams trust us</h2>
            <p>
              Anonymity is fully in your hands: set how confidential each survey is, from completely
              anonymous to fully attributed.
            </p>
          </div>

          <div className="v2v-pillars">
            {PILLARS.map((p) => (
              <article className="v2v-pillar" key={p.title}>
                <CircleIcon>{IconCheck}</CircleIcon>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
                <Link className="link-caps" to="/privacy">
                  Learn more
                </Link>
              </article>
            ))}
          </div>

          <div className="trust-cta">
            <Link className="btn btn-primary" to="/contact">
              Contact me
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
